import * as React from "react";
import Drawer from "@mui/material/Drawer";
import useUkm from "../services/queries/useUkm";
import useUser from "../services/queries/useUser";
import ListItem from "@mui/material/ListItem";
import MenuIcon from "@mui/icons-material/Menu";
import Typography from "@mui/material/Typography";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import StackedBarChartIcon from "@mui/icons-material/StackedBarChart";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import {
    Backdrop,
    Button,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { ParamsGetUkm } from "../constants/types";
import { Link, useLocation, useHistory } from "react-router-dom";
import { getLocalUkmId, setLocalUkmId } from "../utils/handleChangeUkmId";

import {
    AppBar,
    Box,
    Container,
    IconButton,
    Link as MaterialLink,
    Stack,
    Toolbar,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useUkmIsDueExpired from "../services/queries/useUkmIsDueExpired";
import { WhatsApp } from "@mui/icons-material";
import useNoWaAdmin from "../services/queries/useNoWaAdmin";
import logoUloGif from "../assets/images/LoadingUlo.gif";
import ModalTrial from "../components/ModalTrial/ModalTrial";
import ModalAlertPro from "../components/ModalAlertPro/ModalAlertPro";
import moment from "moment";
import useTimeAlt from "../services/queries/useTimeAlt";
import UkmSelector from "./components/UkmSelector";
import SubscriptionBanner from "./components/SubscriptionBanner";
import UpgradeNaikKelasModal from "./components/UpgradeNaikKelasModal";
import useVoucher from "../services/queries/useVoucher";

interface Props {
    children: React.ReactNode;
}

const formatVoucherNominal = (nominal: number) => {
    const numberFormatter = new Intl.NumberFormat("id-ID", {
        maximumFractionDigits: 1,
    });

    if (nominal >= 1_000_000) {
        return `${numberFormatter.format(nominal / 1_000_000)}JT`;
    }
    if (nominal >= 1_000) {
        return `${numberFormatter.format(nominal / 1_000)}RB`;
    }
    return numberFormatter.format(nominal);
};

export default function PageLayout(props: Props) {
    const { children } = props;
    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.between("xs", "sm"));
    const isLaptopScreen = useMediaQuery(theme.breakpoints.up("lg"));
    const { data: user, isLoading: isLoadingUser } = useUser();
    const initialParamsUkmIsDueExpired = React.useMemo(
        () => ({
            search: "",
            size: 5,
            page: 1,
            isDueExpired: 1,
        }),
        [],
    );
    const { data: ukmIsDueExpired } = useUkmIsDueExpired(
        initialParamsUkmIsDueExpired,
    );
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [isOpenModalTrial, setIsOpenModalTrial] = React.useState(false);
    const [isOpenModalAlertPro, setIsOpenModalAlertPro] = React.useState(false);
    const [isOpenUpgradeNaikKelas, setIsOpenUpgradeNaikKelas] =
        React.useState(false);
    const thisYear = new Date().getFullYear();
    const { data: noWaAdmin } = useNoWaAdmin();
    const { data: time } = useTimeAlt();

    const location = useLocation();
    let history = useHistory();

    const openModalTrial = () => setIsOpenModalTrial(true);
    const closeModalTrial = () => setIsOpenModalTrial(false);

    const initialParamsUkm = {
        size: 500,
        page: 1,
        search: "",
    };

    const [paramsUkm, setParamsUkm] =
        React.useState<ParamsGetUkm>(initialParamsUkm);
    const { data: ukm, isLoading: isLoadingUkm } = useUkm(paramsUkm);

    const ukmId = React.useMemo(() => {
        if (user?.userId) {
            return getLocalUkmId(user?.userId);
        }
        return null;
    }, [user]);

    const drawerWidth = 190;

    const ukmData = React.useMemo(() => {
        if (ukmId) {
            return ukm?.content.find((li) => li.id === ukmId);
        }
        return ukm?.content[0];
    }, [ukmId, ukm]);
    const { data: vouchers } = useVoucher({ ukmId: ukmData?.id });
    const availableVoucher = React.useMemo(
        () => vouchers?.find((voucher) => voucher.voucherAmount > 0),
        [vouchers],
    );

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const dateExpiredTime = React.useMemo(
        () => moment(ukmData?.trialTempo),
        [ukmData?.trialTempo],
    );
    const dateNow = React.useMemo(() => moment(), []);
    const expiredTime = dateExpiredTime.diff(dateNow);

    React.useEffect(() => {
        if (user && user?.ukmId?.length > 0) {
            setParamsUkm((prev) => ({
                ...prev,
                userId: user.ukmId[0],
            }));
        }
    }, [user]);

    history.listen(() => {
        setMobileOpen(false);
    });

    const optionsUkm = React.useMemo(() => {
        return (
            ukm?.content?.map((ukmItem) => ({
                value: ukmItem.id,
                label: ukmItem.namaToko,
                data: ukmItem,
            })) || []
        );
    }, [ukm]);

    const selectUkm = React.useMemo(() => {
        if (ukmData && optionsUkm.length > 0) {
            return (
                optionsUkm.find((option) => option.value === ukmData.id) || null
            );
        }
        return null;
    }, [ukmData, optionsUkm]);

    const handleSelectUkmChange = (selectedOption: any) => {
        if (selectedOption && user?.userId) {
            setLocalUkmId(user.userId, selectedOption.value);
            window.location.reload();
        }
    };

    const canSwitchUkm =
        user?.role?.id === "10" ||
        (user?.aksesPindahUkm === 1 && user?.akses?.pindahUkm?.includes(101));

    const laptopDrawer = (
        <Stack flex={1} direction="column" alignItems="flex-start" marginY={2}>
            <Box width="100%">
                <UkmSelector
                    user={user}
                    ukmData={ukmData}
                    isLoadingUkm={isLoadingUkm}
                    isLoadingUser={isLoadingUser}
                    canSwitchUkm={!!canSwitchUkm}
                    selectUkm={selectUkm}
                    optionsUkm={optionsUkm}
                    onChangeUkm={handleSelectUkmChange}
                />

                <Stack direction="column" spacing="13px" alignItems="center">
                    {user?.aksesTransaksi === 1 && (
                        <ListItem component={Link} to="/transaksi">
                            <ListItemButton
                                selected={location.pathname === "/transaksi"}
                                sx={{ borderRadius: 2 }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: "unset",
                                        marginRight: 2,
                                    }}
                                >
                                    <ShoppingCartIcon
                                        sx={{ width: 32, height: 32 }}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primaryTypographyProps={{
                                        color: "#363845",
                                        variant: "h6",
                                        fontSize: 16,
                                    }}
                                    primary="Penjualan"
                                />
                            </ListItemButton>
                        </ListItem>
                    )}
                    {user?.aksesStok === 1 && (
                        <ListItem component={Link} to="/stok">
                            <ListItemButton
                                selected={location.pathname === "/stok"}
                                sx={{ borderRadius: 2 }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: "unset",
                                        marginRight: 2,
                                    }}
                                >
                                    <AllInboxIcon
                                        sx={{
                                            fontSize: "30px",
                                            color: "#363845",
                                        }}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primaryTypographyProps={{
                                        color: "#363845",
                                        variant: "h6",
                                        fontSize: 16,
                                    }}
                                    primary="Stok"
                                />
                            </ListItemButton>
                        </ListItem>
                    )}
                    {user?.aksesLaporan === 1 && (
                        <ListItem component={Link} to="/laporan">
                            <ListItemButton
                                selected={location.pathname === "/laporan"}
                                sx={{ borderRadius: 2 }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: "unset",
                                        marginRight: 2,
                                    }}
                                >
                                    <StackedBarChartIcon
                                        sx={{
                                            fontSize: "30px",
                                            color: "#363845",
                                        }}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primaryTypographyProps={{
                                        color: "#363845",
                                        variant: "h6",
                                        fontSize: 16,
                                    }}
                                    primary="Laporan"
                                />
                            </ListItemButton>
                        </ListItem>
                    )}
                </Stack>
            </Box>
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="100%"
                flexGrow={1}
            />
            <Stack alignItems="center" spacing={2} padding={2} width="100%">
                {ukmData?.isNaikKelas !== 1 && (
                    <Box
                        width="100%"
                        position="relative"
                        padding="16px 12px 12px"
                        sx={{
                            boxSizing: "border-box",
                            bgcolor: "rgba(69, 167, 121, 0.16)",
                            border: "2px solid #45A779",
                            borderRadius: "7px",
                        }}
                    >
                        {availableVoucher && (
                            <Box
                                position="absolute"
                                top={-8}
                                right={-7}
                                padding="5px 10px"
                                sx={{
                                    bgcolor: "#FF3B30",
                                    borderRadius: "6px 4px 4px 6px",
                                    color: "#FFFFFF",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    lineHeight: 1.25,
                                    WebkitMask:
                                        "radial-gradient(circle at left center, transparent 4px, #000 4.5px) left center / 51% 100% no-repeat, radial-gradient(circle at right center, transparent 4px, #000 4.5px) right center / 51% 100% no-repeat",
                                    mask:
                                        "radial-gradient(circle at left center, transparent 4px, #000 4.5px) left center / 51% 100% no-repeat, radial-gradient(circle at right center, transparent 4px, #000 4.5px) right center / 51% 100% no-repeat",
                                }}
                            >
                                Voucher {formatVoucherNominal(availableVoucher.nominal)}
                            </Box>
                        )}

                        <Typography
                            color="#343941"
                            fontSize={17}
                            fontWeight="bold"
                            lineHeight={1.35}
                        >
                            Upgrade ULO Naik Kelas
                        </Typography>
                        <Typography
                            component="div"
                            color="#5F6765"
                            fontSize={14}
                            lineHeight={1.65}
                            marginTop={0.5}
                        >
                            Usaha makin ramai? Lengkapi fiturnya untuk kelola
                            bisnis secara maksimal.{" "}
                            <MaterialLink
                                href="https://ukmlokal.com/fitur/"
                                target="_blank"
                                rel="noreferrer"
                                underline="hover"
                                color="#45A779"
                                sx={{
                                    font: "inherit",
                                }}
                            >
                                Lihat Selengkapnya
                            </MaterialLink>
                        </Typography>
                        <Button
                            variant="contained"
                            color="buttonulo"
                            disableElevation
                            fullWidth
                            onClick={() => setIsOpenUpgradeNaikKelas(true)}
                            sx={{
                                marginTop: 1,
                                borderRadius: "7px",
                                fontWeight: "bold",
                                textTransform: "none",
                            }}
                        >
                            Upgrade Sekarang
                        </Button>
                    </Box>
                )}
                <Button
                    href={`https://wa.me/send?phone=${noWaAdmin?.nomor}&text=Saya%20ingin%20bertanya%20mengenai%20ULO%20Tumbuh%20saya.`}
                    target="_blank"
                    rel="noreferrer"
                    variant="outlined"
                    startIcon={<WhatsApp />}
                    color="whatsapp"
                    fullWidth
                    sx={{ textTransform: "none" }}
                >
                    Hubungi Kami
                </Button>
                <Typography
                    component="div"
                    display="inline-block"
                    color="#999999"
                >
                    <MaterialLink
                        target="_blank"
                        href="https://nore.web.id/"
                        underline="hover"
                        display="inline"
                        color="#999999"
                        fontWeight="bold"
                    >
                        Nore Inovasi © {thisYear}.
                    </MaterialLink>
                    <Typography display="inline" fontWeight="bold">
                        {" "}
                        All rights reserved
                    </Typography>
                </Typography>
            </Stack>
        </Stack>
    );

    const container =
        window !== undefined ? () => window.document.body : undefined;

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const queryUkmId = urlParams.get("redirect");

    return (
        <Box
            sx={{
                display: !!queryUkmId ? "none" : "flex",
                paddingTop: isPhoneScreen
                    ? "56px"
                    : isLaptopScreen
                      ? undefined
                      : "64px",
            }}
        >
            <AppBar
                sx={{
                    display: { xs: "block", lg: "none" },
                    width: { lg: `calc(100% - 280px)` },
                    ml: { sm: `${drawerWidth}px` },
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { lg: "none" } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div">
                        {ukmData?.namaToko}
                    </Typography>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ width: { lg: 280 }, flexShrink: { sm: 0 } }}
                aria-label="mailbox folders"
            >
                <Drawer
                    container={container}
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: "block", lg: "block" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: 280,
                        },
                    }}
                >
                    {laptopDrawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: {
                            xs: "none",
                            sm: "none",
                            md: "none",
                            lg: "block",
                        },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: 280,
                        },
                    }}
                    open
                >
                    {laptopDrawer}
                </Drawer>
            </Box>
            <Box component="main" sx={{ flexGrow: 1 }}>
                <SubscriptionBanner
                    ukmData={ukmData}
                    time={time}
                    ukmIsDueExpired={ukmIsDueExpired}
                    expiredTime={expiredTime}
                    onOpenModalTrial={openModalTrial}
                    onOpenModalAlertPro={() => setIsOpenModalAlertPro(true)}
                />
                <Backdrop
                    sx={{
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                    }}
                    open={isLoadingUser || isLoadingUkm}
                >
                    <img
                        src={logoUloGif}
                        height="100px"
                        width="100px"
                        style={{ objectFit: "contain" }}
                        alt="loading..."
                    />
                    <Typography color="#fff">Memuat Data UKM...</Typography>
                </Backdrop>
                <Container maxWidth="xl">{children}</Container>
            </Box>
            {ukmData && (
                <ModalTrial
                    closeModalTrial={closeModalTrial}
                    isOpenModalTrial={isOpenModalTrial}
                    ukmData={ukmData}
                />
            )}
            {ukmData && (
                <ModalAlertPro
                    isOpenModalAlertPro={isOpenModalAlertPro}
                    closeModalAlertPro={() => setIsOpenModalAlertPro(false)}
                    dataUkm={{ id: ukmData?.id, nama: ukmData?.namaToko }}
                />
            )}
            {ukmData && (
                <UpgradeNaikKelasModal
                    open={isOpenUpgradeNaikKelas}
                    ukmId={ukmData.id}
                    ukmName={ukmData.namaToko}
                    onClose={() => setIsOpenUpgradeNaikKelas(false)}
                />
            )}
        </Box>
    );
}
